import { ContextMenu, Separator } from '@tunein/react-ui';
import type { ActionNames, ChildGuideItem, ResolvedMenuActionItem } from '@tunein/web-api-client';
import clsx from 'clsx';
import type { ComponentType, ReactElement } from 'react';
import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { selectIsMobile } from 'src/common/selectors/app';
import { useShouldShowPlayer } from 'src/common/utils/playerStatus/shouldShowPlayer';
import { CancelMenuItem } from './CancelMenuItem';
import css from './guide-item-long-press-menu.module.scss';
import { type GuideItemMenuItemProps, RemoveRecentMenuItem } from './RemoveRecentMenuItem';

interface GuideItemLongPressMenuProps {
  guideItem?: ChildGuideItem;
  children: ReactElement;
}

interface LongPressMenuProps {
  guideItem: ChildGuideItem;
  menuActions: ResolvedMenuActionItem[];
  children: ReactElement;
}

const menuItemComponents: Partial<Record<ActionNames, ComponentType<GuideItemMenuItemProps>>> = {
  RemoveRecent: RemoveRecentMenuItem,
};

/**
 * Restricted to mobile clients on purpose: `ContextMenu` also opens on right-click, and taking over
 * the browser's own context menu on desktop is not part of this feature (TUNE-19401 is the mobile
 * half; the desktop half shipped in TUNE-18982 with hover-revealed row actions instead).
 */
function LongPressMenu({ guideItem, menuActions, children }: LongPressMenuProps) {
  const isMobile = useSelector(selectIsMobile);
  const isPlayerVisible = useShouldShowPlayer();

  if (!isMobile) {
    return children;
  }

  return (
    // Pinned across the bottom of the viewport rather than anchored to the tile: a native long-press
    // menu is unreachable from web (no API exists, and the platform callout can only be suppressed),
    // so this is the unified stand-in for both Android and iOS.
    <ContextMenu
      classNames={{
        popup: css.menu,
        positioner: clsx(css.positioner, isPlayerVisible && css.positionerAbovePlayer),
      }}
      placement="bottom-sheet"
      trigger={children}
    >
      {menuActions.map((menuAction) => {
        const MenuItem = menuItemComponents[
          menuAction.actionKey
        ] as ComponentType<GuideItemMenuItemProps>;

        return (
          <Fragment key={menuAction.actionKey}>
            <MenuItem guideItem={guideItem} menuAction={menuAction} />
            <Separator className={css.divider} />
          </Fragment>
        );
      })}
      <CancelMenuItem />
    </ContextMenu>
  );
}

/**
 * Wraps a guide item so a long press opens a menu of the actions the API offers on it — the touch
 * counterpart to the hover-revealed row actions on the Recents page (see GuideItemActions), and how
 * a recents tile on the home screen gets removed.
 *
 * Which actions appear is decided entirely by `menuActions`, so there's no client-side allowlist to
 * keep in sync with the API. Deliberately hook-free: it wraps every tile in every gallery, so it
 * returns `children` untouched before anything that reads the store or mounts an action can run.
 */
export function GuideItemLongPressMenu({ guideItem, children }: GuideItemLongPressMenuProps) {
  const opensOnLongPress = guideItem?.behaviors?.longPress?.actionName === 'Menu';
  const menuActions = guideItem?.menuActions?.filter(
    (menuAction) => menuItemComponents[menuAction.actionKey],
  );

  if (!guideItem || !opensOnLongPress || !menuActions?.length) {
    return children;
  }

  return (
    <LongPressMenu guideItem={guideItem} menuActions={menuActions}>
      {children}
    </LongPressMenu>
  );
}
